import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, from, lastValueFrom, of, timer } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { User, VerifyUserRequest } from 'src/app/_shared/models/common';
import { StorageHelper } from 'src/app/_utilities/storage-helper';
import { Router } from '@angular/router';
import { log } from 'mwc/util/other';
import { MixpanelService } from 'src/app/_shared/services/mixpanel/mixpanel.service';
import { SessionInstanceService } from 'src/app/services/session/session-instance.service';

@Injectable({
  providedIn: 'root'
})
export class TokenService {
  private tokenUrl = 'api/auth/InitializeUserSession';
  private newTokenUrl = 'api/auth/getnewtoken';
  private getReturnUrl = 'api/auth/getReturnUrl';
  private tokenSubject = new BehaviorSubject<string | null>(null);
  private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  token$ = this.tokenSubject.asObservable();
  isAuthenticated$ = this.isAuthenticatedSubject.asObservable();
  mixpanelTrackingId: string = '';
  private _storageHelper: StorageHelper;
  private refreshTimer: any;
  private isRefreshing = false;
  private refreshTokenSubject = new BehaviorSubject<any>(null);
  /** In-tab single-flight guard: concurrent callers share one in-flight refresh. */
  private refreshInFlight: Promise<any | null> | null = null;
  /** Web Locks key used to serialise refreshes across same-origin tabs. */
  private static readonly REFRESH_LOCK = 'cs-token-refresh';
  constructor(private http: HttpClient, private router: Router, @Inject('BASE_URL') private baseUrl: string,
    private mixpanelService: MixpanelService, private sessionInstanceService: SessionInstanceService) {
    this._storageHelper = new StorageHelper();
  }

  isTokenExpiringSoon(minutes: number = 10): boolean {
    const expiry = this.getTokenExpiry();
    const bufferTime = minutes * 60 * 1000;
    return expiry ? Date.now() >= (expiry - bufferTime) : true;
  }

  getTokenExpiry(): number | null {
    const expiry = this._storageHelper.user?.expiresAt || null;
    return expiry;
  }

  getAccessToken(): string | null {
    return this._storageHelper.user?.accessToken || null;
  }

  isTokenExpired(): boolean {
    const expiry = this.getTokenExpiry();
    return expiry ? Date.now() >= expiry : true;
  }

  private storeUserData(user: User): void {
    this._storageHelper.user = user;
    this._storageHelper.user = {
      ...this._storageHelper.user
    };
  }

  public async ApplyProfile(profileId: number): Promise<boolean> {
    try {
      const user = await lastValueFrom(
        this.http.post<User>(`${this.baseUrl}api/auth/applyprofile?profileId=` + profileId, {})
      );
      if (user && user.success) {
        this.storeUserData(user);
        // Adopt the active profile's session id (profile switch). Same profile => same id.
        this.sessionInstanceService.adoptActiveSession(user.activeSessionId);
        return true;
      }
    } catch (err) {
      console.log(err);
    }
    this._storageHelper.clear();
    return false;
  }

  async fetchTokenFromApi(request: VerifyUserRequest): Promise<User | null> {
    try {
      let header = new HttpHeaders({ 'Content-Type': 'application/json' });
      const user = await this.http.post<User>(this.baseUrl + this.tokenUrl, request, {
        withCredentials: true,
        headers: header,
      }).toPromise();
      if (user && user.accessToken) {
        this.setToken(user.accessToken);
        const bId = this._storageHelper.user?.bId || '';
        const profileAlias = this._storageHelper.user?.profileAlias || '';
        const contentGenerationId = this._storageHelper.user?.contentGenerationId || '';
        this.storeUserData(user);
        this._storageHelper.user = {
          ...this._storageHelper.user
        };
        if (bId) this._storageHelper.setUserBusinessId(bId);
        if (profileAlias) this._storageHelper.setUserProfileAlias(profileAlias);
        if (contentGenerationId) this._storageHelper.setContentGenerationId(contentGenerationId);
        // Adopt the active profile's session id (initial bootstrap). Same profile => same id.
        this.sessionInstanceService.adoptActiveSession(user.activeSessionId);
        await this.getMixpanelInfo();
        this.scheduleTokenRefresh();
      } else {
        // Session could not be established. Just clear the token here; the caller
        // (auth guard) routes the user to the session-expired screen.
        this.clearToken();
      }
      return user;
    } catch (error) {
      this.clearToken();
      console.error('Failed to fetch token:', error);
      return error;
    }
  }

  async getMixpanelInfo() {
    if (!this.mixpanelTrackingId || this.mixpanelTrackingId === '') {
      const result = this.mixpanelService.getMixpanelId();
      const trackingId = await lastValueFrom(result);
      if (trackingId) {
        this.mixpanelTrackingId = trackingId.mixpanelTrackingId;
        window.mixpanel.init(this.mixpanelTrackingId);
      }
    }
  }

  setToken(token: string): void {
    this.tokenSubject.next(token);
    this.isAuthenticatedSubject.next(true);
  }

  clearToken(): void {
    this.tokenSubject.next(null);
    this.isAuthenticatedSubject.next(false);
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }
    this.isRefreshing = false;
    this.refreshTokenSubject.next(null);
  }

  getTokenValue(): string | null {
    return this.tokenSubject.getValue();
  }

  getExpiryTimestampValue(): number | null {
    return this._storageHelper.user?.expiresAt || null;
  }

  /**
   * Single-flight token refresh, coordinated ACROSS browser tabs.
   *
   * - Within a tab, concurrent callers (HTTP interceptor + scheduled timer + auth guard)
   *   share one in-flight refresh via `refreshInFlight`.
   * - Across tabs of the same origin, the Web Locks API serialises refreshes so only ONE
   *   tab calls the backend at a time. Midgard rotates refresh tokens as one-time-use, so
   *   two tabs refreshing at once would make one fail with CannotRefreshToken (and log the
   *   user out). The lock prevents that.
   * - Whoever waits for the lock re-checks first: if another tab/app already produced a
   *   newer token (now in localStorage / the shared cookie), it ADOPTS that instead of
   *   refreshing again.
   */
  refreshToken(): Observable<any> {
    if (!this.refreshInFlight) {
      this.refreshInFlight = this.runCoordinatedRefresh().finally(() => {
        this.refreshInFlight = null;
      });
    }
    return from(this.refreshInFlight);
  }

  private runCoordinatedRefresh(): Promise<any | null> {
    // Capture the token we are trying to replace BEFORE waiting for the cross-tab lock.
    const tokenAtRequest = this.getAccessToken();

    const doRefresh = async (): Promise<any | null> => {
      // Another tab may have refreshed while we waited for the lock -> adopt its token,
      // no network call (which would otherwise reuse a now-rotated refresh token).
      const current = this.getAccessToken();
      if (current && current !== tokenAtRequest) {
        this.setToken(current);
        this.scheduleTokenRefresh();
        return { access_token: current };
      }
      // We hold the lock and the token is unchanged -> perform the real network refresh.
      try {
        const result = await lastValueFrom(this.getNewAccessToken());
        if (result?.access_token) {
          this.scheduleTokenRefresh();
        } else {
          this.clearToken();
        }
        return result ?? null;
      } catch {
        this.clearToken();
        return null;
      }
    };

    const locks = (typeof navigator !== 'undefined') ? (navigator as any).locks : undefined;
    if (locks && typeof locks.request === 'function') {
      // Serialise across all same-origin tabs.
      return locks.request(TokenService.REFRESH_LOCK, doRefresh);
    }
    // Web Locks unavailable -> in-tab single-flight only (refreshInFlight already guards this tab).
    return doRefresh();
  }

  private requestNewToken(): Observable<any> {
    let header = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.post<any>(this.baseUrl + this.newTokenUrl, '', {
      withCredentials: true,
      headers: header,
    });
  }

  /** Adopt a token returned by getnewtoken (the backend may have rotated it OR
   *  handed back a token Asgard/another tab already rotated into the shared cookie). */
  private applyNewToken(token: any): Observable<any | null> {
    this.setToken(token.access_token);
    this._storageHelper.user = {
      ...this._storageHelper.user,
      accessToken: token.access_token,
      expiresAt: token.expires_in,
    };
    if (token.requires_reinitialize) {
      const request = new VerifyUserRequest();
      request.businessId = +(this._storageHelper.user?.bId || 0);
      request.profileAlias = this._storageHelper.user?.profileAlias || '';
      return from(this.fetchTokenFromApi(request)).pipe(
        map(user => user ? { access_token: user.accessToken, expires_in: user.expiresAt } : null)
      );
    }
    return of(token);
  }

  getNewAccessToken(): Observable<any | null> {
    return this.requestNewToken().pipe(
      switchMap(token => {
        if (token?.access_token) return this.applyNewToken(token);
        // Empty response. The other app (Asgard) or another tab may be rotating the token
        // into the shared cookie right now. Retry once after a short delay so we adopt that
        // token instead of logging the user out on a transient rotation race.
        return timer(1000).pipe(
          switchMap(() => this.requestNewToken()),
          switchMap(retryToken => retryToken?.access_token ? this.applyNewToken(retryToken) : of(null))
        );
      }),
      catchError(() => {
        this.clearToken();
        return of(null);
      })
    );
  }

  private scheduleTokenRefresh(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }
    const expiry = this.getTokenExpiry();
    if (!expiry) return;
    // Production
    const refreshTime = expiry - Date.now() - (10 * 60 * 1000);
    // Development: refresh after 4 minutes
    //const refreshTime = 4 * 60 * 1000;
    if (refreshTime > 0) {
      this.refreshTimer = setTimeout(() => {
        this.refreshToken().subscribe({
          next: (result) => {
            if (result) {
              console.log('Token refreshed automatically');
            } else {
              // Proactive refresh failed -> session can no longer be sustained.
              // Show the timeout screen without waiting for the user to act.
              this.routeToSessionExpired();
            }
          },
          error: (error) => {
            console.error('Auto token refresh failed:', error);
            this.routeToSessionExpired();
          }
        });
      }, refreshTime);
    }
  }

  /**
   * Navigate to the full-page "session expired" screen. Centralised so the
   * proactive refresh timer and the reactive 401 handler land on the same page.
   */
  routeToSessionExpired(): void {
    this.clearToken();
    this.router.navigate(['/session-expired']);
  }

  routeToBase(url: string = "") {
    const redirectUrl = this._storageHelper.user ? (this._storageHelper.user.returnUrl != "" ? this._storageHelper.user.returnUrl : url) : url;
    if (redirectUrl) {
      window.location.href = redirectUrl;
    } else {
      this.router.navigate(['auth']);
    }
  }

  async fetchReturnUrl() {
    const result = this.getBaseReturnUrl();
    const url = await lastValueFrom(result);
    if (url && url.url) {
      this.routeToBase(url.url);
    }
  }

  getBaseReturnUrl(): Observable<any> {
    let header = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.get<any>(`${this.baseUrl}${this.getReturnUrl}`, {
      withCredentials: false,
      headers: header,
    });
  }
}
